import { useState, useEffect, useCallback, useRef } from 'react';
import { isInputFocused } from './useKeyboardShortcuts';

/**
 * Reusable card navigation hook for list panels.
 * Provides arrow key navigation (↑/↓), Enter to select, and focused index state.
 *
 * @param {Object} opts
 * @param {number}   opts.itemCount          - Total number of navigable items
 * @param {boolean}  opts.enabled            - Whether arrow nav is active (false = disabled, e.g. when modal is open)
 * @param {function} opts.onSelect           - (index: number) => void — called on Enter
 * @param {function} opts.onEdit             - (index: number) => void — called on 'e' key (optional)
 * @param {function} opts.onDelete           - (index: number) => void — called on Delete/Backspace (optional)
 * @param {string}   opts.scrollContainerSelector - CSS selector for the scroll container (used for auto-scroll)
 *
 * @returns {{ focusedIndex: number|null, setFocusedIndex: function, clearFocus: function }}
 */
export function useCardNavigation({
    itemCount,
    enabled = true,
    onSelect,
    onEdit,
    onDelete,
    scrollContainerSelector,
}) {
    const [focusedIndex, setFocusedIndex] = useState(null);
    const containerRef = useRef(null);

    const clearFocus = useCallback(() => setFocusedIndex(null), []);

    // Auto-scroll focused card into view
    useEffect(() => {
        if (focusedIndex === null) return;

        // Find the focused card element by data attribute
        const card = document.querySelector(`[data-card-index="${focusedIndex}"]`);
        if (card) {
            card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [focusedIndex]);

    useEffect(() => {
        if (!enabled || itemCount === 0) return;

        const handler = (e) => {
            if (isInputFocused()) return;

            const key = e.key;

            switch (key) {
                case 'ArrowDown':
                case 'j': {
                    e.preventDefault();
                    setFocusedIndex((prev) => {
                        if (prev === null) return 0;
                        return Math.min(prev + 1, itemCount - 1);
                    });
                    break;
                }
                case 'ArrowUp':
                case 'k': {
                    e.preventDefault();
                    setFocusedIndex((prev) => {
                        if (prev === null) return itemCount - 1;
                        return Math.max(prev - 1, 0);
                    });
                    break;
                }
                case 'Enter': {
                    if (focusedIndex !== null) {
                        e.preventDefault();
                        onSelect?.(focusedIndex);
                    }
                    break;
                }
                case 'e': {
                    if (focusedIndex !== null && onEdit) {
                        e.preventDefault();
                        onEdit(focusedIndex);
                    }
                    break;
                }
                case 'Delete':
                case 'Backspace': {
                    if (focusedIndex !== null && onDelete) {
                        e.preventDefault();
                        onDelete(focusedIndex);
                    }
                    break;
                }
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [enabled, itemCount, focusedIndex, onSelect, onEdit, onDelete]);

    // Reset focus when item count changes (e.g., after delete)
    useEffect(() => {
        if (focusedIndex !== null && focusedIndex >= itemCount) {
            setFocusedIndex(itemCount > 0 ? itemCount - 1 : null);
        }
    }, [itemCount, focusedIndex]);

    return { focusedIndex, setFocusedIndex, clearFocus };
}
